import { useCallback, FC } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { translate } from '@utils';
import { Button, Tooltip } from '@material-ui/core';
import { DeleteModal } from '@components/Modals';
import { makeDeleteSubmissions, setCheckedSubmissions } from '@actions/submissionActions';
import { setIsOpenDeleteModal } from '@actions/lettersPageActions';
import { MgState } from '@types';
import { TrashIcon } from '@images/icons';
import {
  currentSubmissionIdSelector,
  currentSubmissionIsSampleSelector,
  submissionDeletableSelector,
} from '@selectors/submission';
import { DeleteSampleModal } from '@components/Modals/DeleteSampleModal';

export interface DeleteControllerProps {
  description: string;
}

export const DeleteController: FC<DeleteControllerProps> = ({ description }) => {
  const dispatch = useDispatch();
  const isOpenDeleteModal = useSelector<MgState, boolean>(
    ({ lettersPage }) => lettersPage.isOpenDeleteModal
  );
  const isSubmissionsDeleteLoading = useSelector<MgState, boolean>(
    ({ submission }) => submission.isSubmissionsDeleteLoading
  );
  const isSubmissionSample = useSelector(currentSubmissionIsSampleSelector);
  const currentSubmissionId = useSelector(currentSubmissionIdSelector);
  const submissionDeletable = useSelector(submissionDeletableSelector);

  const checkedSubmissions = useSelector<MgState, string[]>(
    ({ submission }) => submission.checkedSubmissions,
    shallowEqual
  );

  const handleCancelDelete = useCallback(() => {
    dispatch(setIsOpenDeleteModal(false));
  }, [isOpenDeleteModal]);

  const handleDelete = useCallback(() => {
    dispatch(makeDeleteSubmissions({ submissionIds: [currentSubmissionId] }));
    dispatch(setCheckedSubmissions(checkedSubmissions.filter((id) => id !== currentSubmissionId)));
  }, [checkedSubmissions, currentSubmissionId]);

  return (
    <>
      <Tooltip title={isSubmissionSample ? translate('delete_sample') : description} arrow>
        <span>
          <Button
            size="large"
            color={!isSubmissionSample ? 'primary' : 'default'}
            onClick={() => dispatch(setIsOpenDeleteModal(true))}
            disabled={!submissionDeletable}
            disableRipple
          >
            <TrashIcon />
          </Button>
        </span>
      </Tooltip>
      {isSubmissionSample ? (
        <DeleteSampleModal
          open={isOpenDeleteModal}
          handleCancel={handleCancelDelete}
          isDisabledConfirmationButton={isSubmissionsDeleteLoading}
        />
      ) : (
        <DeleteModal
          open={isOpenDeleteModal}
          handleCancel={handleCancelDelete}
          handleConfirmation={handleDelete}
          contentName="caution_message"
          title={translate('delete_question')}
          isDisabledConfirmationButton={isSubmissionsDeleteLoading}
        />
      )}
    </>
  );
};
